import { useEffect } from "react";

/**
 * Lightweight per-route SEO hook. Sets a unique <title> and updates the
 * <meta name="description"> + <link rel="canonical"> tags on mount. This is
 * enough for search engines (Google renders the SPA and reads the DOM after
 * JS execution) and avoids adding a heavy dep like react-helmet-async for
 * just 4 public routes.
 *
 * `path` is used to build the canonical URL. Defaults to the current
 * `window.location.pathname` so callers can omit it in the common case.
 */
export function useSEO({ title, description, path, noIndex = false } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (created) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute("content", prev);
      };
    };

    const restoreDesc = setMeta("description", description);
    const restoreOgTitle = setMeta("og:title", title, "property");
    const restoreOgDesc = setMeta("og:description", description, "property");
    const restoreRobots = noIndex ? setMeta("robots", "noindex, nofollow") : null;

    // Canonical <link>. Build from window.location + optional override.
    const canonicalHref = `${window.location.origin}${path || window.location.pathname}`;
    let link = document.head.querySelector('link[rel="canonical"]');
    const linkCreated = !link;
    if (linkCreated) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const prevHref = link.getAttribute("href");
    link.setAttribute("href", canonicalHref);

    return () => {
      document.title = prevTitle;
      restoreDesc?.();
      restoreOgTitle?.();
      restoreOgDesc?.();
      restoreRobots?.();
      if (linkCreated) link.remove();
      else if (prevHref) link.setAttribute("href", prevHref);
    };
  }, [title, description, path, noIndex]);
}
